import * as Sentry from "@sentry/react";
import { useAuth } from "@workos-inc/authkit-react";
import { type ReactNode, useEffect } from "react";
import { ApplicationVersionErrorFallback } from "@/components/application-version-error-fallback";
import { GenericErrorFallback } from "@/components/generic-error-fallback";
import { Spinner } from "@/components/ui/spinner";
import { isChunkLoadError } from "@/lib/connection-errors";
import { isAuthError } from "@/lib/convex-errors";

// ============================================================================
// Fallback Components
// ============================================================================

/**
 * Loading screen shown while redirecting to login.
 */
function AuthErrorFallback({ signIn }: { signIn: () => void }) {
  useEffect(() => {
    const timer = setTimeout(signIn, 100);
    return () => clearTimeout(timer);
  }, [signIn]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <Spinner className="mx-auto mb-4 size-8 text-primary" />
        <p className="text-muted-foreground text-sm">Session expired. Redirecting to login...</p>
      </div>
    </div>
  );
}

// ============================================================================
// Public API
// ============================================================================

interface AuthErrorBoundaryProps {
  children: ReactNode;
}

/**
 * Auth-aware error boundary using Sentry's ErrorBoundary.
 *
 * Catches errors and handles them appropriately:
 * - Auth errors: Redirects to login
 * - Other errors: Shows fallback UI and reports to Sentry
 *
 * Sentry filtering is configured separately in `lib/sentry.ts`: expected
 * missing-identity errors are dropped, but invalid auth remains visible.
 */
export function AuthErrorBoundary({ children }: AuthErrorBoundaryProps) {
  const { signIn } = useAuth();

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => {
        if (isAuthError(error)) {
          return <AuthErrorFallback signIn={signIn} />;
        }
        if (isChunkLoadError(error)) {
          return <ApplicationVersionErrorFallback />;
        }
        return <GenericErrorFallback resetError={resetError} />;
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
